import React, { useState, useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import {
  Card,
  Row,
  Col,
  Button,
  Skeleton,
  Divider,
  message,
  Typography,
  Modal,
} from 'antd'
import { getVolunteerOpportunityWithStats } from 'store/volunteerOpportunitySlice/volunteerOpportunityActions'
import { useAuthSelector } from 'store/authSlice/authReducer'
import { ComponentViewHeader } from 'components/common/componentView/components'
import { CopyBlock, dracula } from 'react-code-blocks'
import {
  compareDateTimeWithNow,
  getVolunteerOpportunityUrl,
  htmlCode,
} from 'utils'
import {
  CalendarOutlined,
  ClockCircleOutlined,
  CodeOutlined,
  EnvironmentOutlined,
  HourglassOutlined,
  PrinterOutlined,
} from '@ant-design/icons'
import moment from 'moment'
import sampleImage from 'assets/images/sample/fundraiser-default-image.png'
import { VolunteerList } from './VolunteerList'

const formateDate = (date) => moment(date).format('DD MMMM YY')
const formateTime = (date) => moment(date).format('hh:mm A')

const OpportunityView = () => {
  const history = useHistory()
  const params = useParams()
  const { organization } = useAuthSelector()
  const [refresh, setRefresh] = useState(false)
  const { Title, Paragraph } = Typography
  const [opportunityDetail, setOpportunityDetail] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showEmbedCode, setShowEmbedCode] = useState(false)

  useEffect(() => {
    const getData = async () => {
      if (params.id) {
        setLoading(true)
        const record = await getVolunteerOpportunityWithStats(params.id)
        if (record) {
          setOpportunityDetail(record)
          setLoading(false)
        } else {
          message.error('Invalid Opportunity')
          history.push('/settings/create-volunteer-opportunities/')
        }
      }
    }
    getData()
  }, [params.id, history, refresh])

  const printQR = (type) => {
    message.loading('Generating QR code')
    window.open(`/volunteer-opportunities/${params.id}/qr/${type}`, '_blank')
  }

  const handleEditClick = () => {
    history.push(`/settings/create-volunteer-opportunities/${params.id}`)
  }

  return (
    <Card
      bordered={false}
      className="header-solid card-product mb-24"
      title={<ComponentViewHeader heading="Volunteer Opportunity Detail" />}
    >
      <Skeleton loading={loading} active paragraph={{ rows: 18 }}>
        {opportunityDetail ? (
          <>
            <Row>
              <Col
                span={24}
                xs={24}
                sm={24}
                md={20}
                lg={10}
                xl={8}
                className="col-gallery t-mb-4 md:t-mb-0"
              >
                <img
                  src={
                    opportunityDetail?.bannerImages?.length
                      ? opportunityDetail?.bannerImages?.[0]
                      : sampleImage
                  }
                  className="t-h-40 md:t-h-60 t-w-full t-rounded-lg shadow-3 t-object-cover"
                  alt="Volunteer Opportunity"
                />
              </Col>
              <Col span={24} xs={24} sm={24} md={24} lg={{ offset: 1 }} xl={14}>
                <div className="t-mb-4">
                  <Row
                    justify="space-between" // Add space between Title and Button
                    className="t-my-2"
                  >
                    <Col>
                      <Title level={1} className="t-text-secondary-100">
                        {opportunityDetail?.title}
                      </Title>
                    </Col>
                    <Col>
                      <Button
                        type="primary"
                        className="t-my-1 t-w-full sm:t-w-auto" // Added full width on small screens
                        style={{
                          fontSize: '18px',
                          display: 'flex',
                          flexWrap: 'nowrap',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        onClick={() => handleEditClick()}
                      >
                        Edit
                      </Button>
                    </Col>
                  </Row>
                  <Paragraph className="t-my-4">
                    {opportunityDetail?.description}
                  </Paragraph>

                  <Row className="t-my-6 t-flex t-flex-col md:t-flex-row">
                    {' '}
                    {/* Changed to column on mobile */}
                    <Col
                      span={24}
                      xs={24}
                      sm={12}
                      md={10}
                      lg={14}
                      xl={10}
                      className="t-mb-2 md:t-mb-0"
                    >
                      {' '}
                      {/* Added responsive spans and margin bottom */}
                      <EnvironmentOutlined
                        style={{
                          fontSize: '18px',
                          marginRight: '10px',
                        }}
                      />
                      <span style={{ fontSize: '18px' }}>
                        {opportunityDetail?.address || '--'}
                      </span>
                    </Col>
                    <Col
                      span={24}
                      xs={24}
                      sm={12}
                      md={8}
                      lg={6}
                      xl={6}
                      className="t-mb-2 md:t-mb-0"
                    >
                      {' '}
                      {/* Added responsive spans and margin bottom */}
                      <CalendarOutlined
                        style={{
                          fontSize: '18px',
                          marginRight: '10px',
                          display: 'inline',
                        }}
                      />
                      <span style={{ fontSize: '18px' }}>
                        {opportunityDetail?.scheduleDate
                          ? formateDate(opportunityDetail?.scheduleDate)
                          : '--'}
                      </span>
                    </Col>
                    <Col
                      span={24}
                      xs={24}
                      sm={12}
                      md={8}
                      lg={6}
                      xl={6}
                      className="t-mb-2 md:t-mb-0"
                    >
                      {' '}
                      {/* Added responsive spans and margin bottom */}
                      <ClockCircleOutlined
                        style={{
                          fontSize: '18px',
                          marginRight: '10px',
                          display: 'inline',
                        }}
                      />
                      <span style={{ fontSize: '18px' }}>
                        {opportunityDetail?.scheduleDate
                          ? formateTime(opportunityDetail?.scheduleDate)
                          : '--'}
                      </span>
                    </Col>
                    <Col
                      span={24}
                      xs={24}
                      sm={12}
                      md={8}
                      lg={6}
                      xl={6}
                      className="t-mb-2 md:t-mb-0"
                    >
                      {' '}
                      {/* Added responsive spans and margin bottom */}
                      <HourglassOutlined
                        style={{
                          fontSize: '18px',
                          marginRight: '10px',
                          display: 'inline',
                        }}
                      />
                      {(opportunityDetail?.volunteerDuration && (
                        <span style={{ fontSize: '18px' }}>
                          {opportunityDetail?.volunteerDuration} Hour(s)
                        </span>
                      )) ||
                        '--'}
                    </Col>
                  </Row>

                  <div className="t-flex t-space-x-2 t-flex-wrap">
                    <Title
                      level={4}
                      style={{ marginTop: '8px', marginRight: '10px' }}
                    >
                      Print QR code:
                    </Title>
                    <Button
                      type="primary"
                      className="t-my-1 t-w-full sm:t-w-auto"
                      style={{
                        fontSize: '18px',
                        display: 'flex',
                        flexWrap: 'nowrap',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      onClick={() => printQR('checkin')}
                    >
                      <PrinterOutlined
                        style={{ fontSize: '18px', display: 'inline' }}
                      />{' '}
                      Check-In / Check-Out
                    </Button>
                  </div>
                  <div className="t-flex t-space-x-2 t-flex-wrap">
                    <Title
                      level={4}
                      style={{ marginTop: '8px', marginRight: '10px' }}
                    >
                      Embed Code:
                    </Title>
                    <Button
                      type="primary"
                      className="t-my-1 t-w-full sm:t-w-auto"
                      style={{
                        fontSize: '18px',
                        display: 'flex',
                        flexWrap: 'nowrap',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      onClick={() => setShowEmbedCode(true)}
                    >
                      <CodeOutlined
                        style={{ fontSize: '18px', display: 'inline' }}
                      />{' '}
                      Copy Embed Code
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="t-my-4" gutter={{ xs: 0, md: 16 }}>
              <Col className="t-mt-4" xs={24} sm={12} md={8} lg={6} xl={6}>
                <Card>
                  <Row justify="space-between" align="middle">
                    <Col>
                      <Title level={2}>
                        {compareDateTimeWithNow(
                          opportunityDetail?.scheduleDate,
                          'after',
                        )
                          ? opportunityDetail?.noShow
                          : 0}
                      </Title>
                      <p>No-Show</p>
                    </Col>
                    <Col>
                      <div>
                        <CalendarOutlined
                          style={{ fontSize: '24px', color: 'red' }}
                        />
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
              <Col className="t-mt-4" xs={24} sm={12} md={8} lg={6} xl={6}>
                <Card>
                  <Row justify="space-between" align="middle">
                    <Col>
                      <Title level={2}>
                        {opportunityDetail?.averageDuration}
                      </Title>
                      <p>Average Duration</p>
                    </Col>
                    <Col>
                      <div>
                        <ClockCircleOutlined
                          style={{ fontSize: '24px', color: '#14B1E0' }}
                        />
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>

            <Card className="registered-volunteers-table">
              <Title className="t-font-bold t-text-base" level={6}>
                Registered Volunteers
              </Title>
              <Divider />
              <VolunteerList
                opportunity={opportunityDetail}
                data={opportunityDetail?.opportunityMembers}
                refresh={() => setRefresh((prev) => !prev)}
              />
            </Card>
          </>
        ) : (
          <div className="ant-result ant-result-404">
            <p>Error loading data</p>
          </div>
        )}
      </Skeleton>

      <Modal
        centered
        title="Embed URL"
        width={600}
        open={showEmbedCode}
        onCancel={() => setShowEmbedCode(false)}
        footer={null}
      >
        <div>
          <div className="t-my-2">
            <Title level={4}>Copy iframe code</Title>
            <CopyBlock
              language="html"
              text={`
                      <iframe
                      style="width: 100%; height: 100%"
                      src="${getVolunteerOpportunityUrl(
                        opportunityDetail?.iD,
                        organization?.OrgDomain,
                      )}?embed=true"
                      title="${opportunityDetail?.title}"
                      ></iframe>`}
              theme={dracula}
              wrapLines
              codeBlock
            />
          </div>
          <div className="t-my-2">
            <Title level={4}>Copy code to paste in the website</Title>
            <CopyBlock
              language="html"
              text={htmlCode(
                `${getVolunteerOpportunityUrl(
                  opportunityDetail?.iD,
                  organization?.OrgDomain,
                )}?embed=true`,
                'Opportunity',
              )}
              theme={dracula}
              wrapLines
              codeBlock
            />
          </div>
        </div>
      </Modal>
    </Card>
  )
}

export { OpportunityView }
